import React, { useMemo } from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { electricGreen, textBlueLight } from '@/components/Basic/colors'

const SPEED_COLORS = ['#E2E4E8', '#CCFAE7', '#99F5CE', '#66F0B6', '#33EB9E', '#00E786', '#00B86B']

const FanToggleGroup = styled(ToggleButtonGroup)({
    gap: 6,
    '& .MuiToggleButtonGroup-grouped': {
        border: '1px solid #E2E4E8',
        borderRadius: '8px !important',
        margin: 0,
        '&:not(:first-of-type)': {
            borderLeft: '1px solid #E2E4E8',
        },
        '&.Mui-selected': {
            backgroundColor: '#CCFAE7',
            borderColor: electricGreen,
            color: '#0c446f',
            '&:hover': {
                backgroundColor: '#99F5CE',
            },
        },
    },
})

const FanToggle = styled(ToggleButton)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '6px 10px',
    minWidth: 48,
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.2,
    color: textBlueLight,
})

const IntensityBars = ({ level, maxLevel, active }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 16 }}>
        {Array.from({ length: maxLevel }, (_, i) => {
            const barLevel = i + 1
            const isLit = active && barLevel <= level
            const colorIndex = Math.min(Math.round((barLevel / maxLevel) * 6), 6)
            return (
                <Box
                    key={barLevel}
                    sx={{
                        width: 4,
                        height: 4 + i * 3,
                        borderRadius: '2px',
                        backgroundColor: isLit ? SPEED_COLORS[colorIndex] : '#E2E4E8',
                        transition: 'background-color 0.2s ease',
                    }}
                />
            )
        })}
    </Box>
)

const FanComponent = ({ fanSpeed, onClick, disabled }) => {
    const options = useMemo(() => {
        const list = fanSpeed?.List
        const filtered = (fanSpeed?.options ?? []).filter((option) => {
            if (!list) return true
            return list.indexOf(option?.NameLocal) > -1
        })
        return filtered.sort((a, b) => a.IdGlobal - b.IdGlobal)
    }, [fanSpeed?.options, fanSpeed?.List])

    const selectedValue = fanSpeed?.data?.[0]?.Value

    if (!options.length) return null

    return (
        <FanToggleGroup
            value={selectedValue}
            exclusive
            disabled={disabled}
            onChange={(_, value) => {
                if (value !== null) onClick(value)
            }}
            aria-label="Fan speed"
        >
            {options.map((option, index) => (
                <Tooltip key={option.IdGlobal} title={option.Name} arrow>
                    <FanToggle value={option.IdGlobal} aria-label={option.Name}>
                        <IntensityBars
                            level={index + 1}
                            maxLevel={options.length}
                            active={selectedValue === option.IdGlobal}
                        />
                        {option.Name}
                    </FanToggle>
                </Tooltip>
            ))}
        </FanToggleGroup>
    )
}

export default FanComponent
